import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaClipboardList } from 'react-icons/fa';
import { Badge, Table } from 'react-bootstrap';
import api from '../../api/axios';
import './RoleRequestHistory.css';

const STATUS_VARIANTS = { pending: 'warning', approved: 'success', rejected: 'danger' };

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '—');

const RoleRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setIsLoading(true);
    api.get(`/api/role-requests/${id}`)
      .then((res) => {
        setRequest(res.data);
        setError('');
      })
      .catch((err) => {
        const status = err.response?.status;
        if (status === 403 || status === 404) {
          setError('This role request is not available.');
        } else {
          setError('Failed to load the role request.');
        }
      })
      .finally(() => setIsLoading(false));
  }, [id]);

  if (isLoading) {
    return (
      <div className="rrh-container">
        <div className="rrh-loading">Loading role request...</div>
      </div>
    );
  }

  const decisionForStep = (step) => request?.approvals?.find((a) => a.step === step);

  return (
    <div className="rrh-container">
      <div className="rrh-header">
        <div className="rrh-header-content">
          <div className="rrh-title-section">
            <FaClipboardList className="rrh-icon" />
            <div>
              <h1 className="rrh-title">Role Request #{id}</h1>
              {request && (
                <p className="rrh-subtitle">
                  {request.role} at {request.company}
                </p>
              )}
            </div>
          </div>
          {request && (
            <Badge bg={STATUS_VARIANTS[request.status] ?? 'secondary'} className="fs-6 text-capitalize">
              {request.status}
            </Badge>
          )}
        </div>
      </div>

      {error && <div className="rrh-error-banner">{error}</div>}

      {request && (
        <div className="rrh-content">
          <div className="rrh-header">
            <Table borderless size="sm" className="mb-0">
              <tbody>
                <tr><th style={{ width: '30%' }}>Company</th><td>{request.company}</td></tr>
                <tr><th>Requested role</th><td>{request.role}</td></tr>
                <tr><th>Previous role</th><td>{request.previous_role ?? '—'}</td></tr>
                <tr>
                  <th>Current step</th>
                  <td>
                    {request.status === 'pending' && request.current_step_info
                      ? `Step ${request.current_step} of ${request.total_steps}: ${request.current_step_info.role_name} (${request.current_step_info.company_name})`
                      : request.status === 'pending' ? `Step ${request.current_step}` : 'Finished'}
                  </td>
                </tr>
                <tr><th>Submitted</th><td>{formatDate(request.created_at)}</td></tr>
                <tr><th>Last updated</th><td>{formatDate(request.updated_at)}</td></tr>
                {request.rejection_reason && (
                  <tr><th>Rejection reason</th><td className="text-danger">{request.rejection_reason}</td></tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="rrh-header">
            <h5 className="mb-3">Approval line</h5>
            {request.approval_chain?.length > 0 ? (
              <Table bordered hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Step</th>
                    <th>Approver role</th>
                    <th>Company</th>
                    <th>Decision</th>
                    <th>By</th>
                    <th>When</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {request.approval_chain.map((step) => {
                    const decision = decisionForStep(step.step);
                    return (
                      <tr key={step.step}>
                        <td>{step.step}</td>
                        <td>{step.role_name}</td>
                        <td>{step.company_name}</td>
                        <td className="text-capitalize">{decision?.action ?? 'waiting'}</td>
                        <td>{decision?.approver ?? '—'}</td>
                        <td>{formatDate(decision?.action_at)}</td>
                        <td>{decision?.rejection_reason ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : (
              <p className="mb-0 text-muted">No approvers were required for this request.</p>
            )}
          </div>
        </div>
      )}

      <div className="rrh-footer">
        <button onClick={() => navigate('/role-requests')} className="rrh-back-btn">
          <FaArrowLeft /> Back to Request History
        </button>
      </div>
    </div>
  );
};

export default RoleRequestDetail;
